import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ProductsTable } from "../../../components/dashboard/products/products-table";
import { getProductsAsync } from "../../../slices/products";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ArrowUpIcon,
  CheckBadgeIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import ProductModal from "../../../components/dashboard/products/ProductModal";
import { updateUrlWithException } from "../../../components/Utils";
import { ProductActionEditBar } from "../../../components/dashboard/products/product-action-edit-bar";
import ProductMultiEditDialog from "../../../components/dashboard/products/product-multi-edit-dialog";
import ProductPriceChangeDialog from "../../../components/dashboard/products/product-price-change-dialog";
import { debounce } from "lodash";
import { ModuleFilter } from "../../../components/utils/ModuleFilter";
import { getProductCategoriesAsync } from "../../../slices/productCategory";

/** Build API filter array from shareable URL flags (products list). */
function parseProductListFiltersFromSearchParams(searchParams) {
  const filters = [];
  if (searchParams.get("productActive") !== "0") {
    filters.push({ name: "active", value: "true" });
  }
  if (searchParams.get("productWh") === "1") {
    filters.push({
      name: "warehouse_status_type",
      value: JSON.stringify([1, 2, 3]),
    });
  }
  if (searchParams.get("productUncat") === "1") {
    filters.push({ name: "uncategorized", value: "true" });
  }
  return filters;
}

function readInitialProductListController() {
  const sp =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  return {
    filters: parseProductListFiltersFromSearchParams(sp),
    page: sp.get("page") || 1,
    per_page: sp.get("per_page") || 25,
    query: sp.get("query") || "",
    sort: sp.get("sort") || "asc",
    sortBy: sp.get("sortBy") || "created_at",
    fields:
      "id,displayTitle,title,item_number,brand,status,warehouse_status_type_id,purchase_price,sales_price,model_year,main_picture.id",
  };
}

export const ProductsList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const productState = useSelector((store) => {
    return store.products;
  });

  const categoryState = useSelector((store) => {
    return store.productCategories || { data: [], isLoading: false };
  });

  const { isLoading } = productState;
  const [selectedItems, setSelectedItems] = useState([]);
  const [controller, setController] = useState(readInitialProductListController);

  const handleProductSort = (column) => {
    const currentSortBy = controller.sortBy || "created_at";
    const currentSort = controller.sort || "asc";
    const newSort =
      currentSortBy === column
        ? currentSort.toLowerCase() === "desc"
          ? "asc"
          : "desc"
        : "desc";
    setController({
      ...controller,
      sortBy: column,
      sort: newSort,
      page: 1,
    });
  };

  // Sync URL (page, search, sort, product filter toggles). Preserves unrelated `filters` param if present.
  useEffect(() => {
    const hasWh = controller.filters.some(
      (f) => f.name === "warehouse_status_type"
    );
    const hasActive = controller.filters.some((f) => f.name === "active");
    const hasUncat = controller.filters.some(
      (f) => f.name === "uncategorized"
    );
    updateUrlWithException(
      {
        page: controller.page,
        per_page: controller.per_page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        productWh: hasWh ? "1" : "",
        productActive: hasActive ? "1" : "0",
        productUncat: hasUncat ? "1" : "",
      },
      "filters"
    );
  }, [controller]);

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handlePagePageChange = (option) => {
    setController({
      ...controller,
      per_page: option,
    });
  };

  // Handle ModuleFilter changes (for category and checkbox filters)
  const handleModuleFilterChange = (filters) => {
    const backendFilters = [];
    
    // Add category filter if categories are selected
    if (filters.categories && filters.categories.length > 0) {
      backendFilters.push({
        name: "category",
        value: JSON.stringify(filters.categories),
      });
    }
    
    // Add active filter if checked
    if (filters.active === true) {
      backendFilters.push({
        name: "active",
        value: "true",
      });
    }
    
    // Add uncategorized filter if checked
    if (filters.uncategorized === true) {
      backendFilters.push({
        name: "uncategorized",
        value: "true",
      });
    }
    
    // Add warehouse_status_type filter if lagerforte is checked
    if (filters.lagerforte === true) {
      backendFilters.push({
        name: "warehouse_status_type",
        value: JSON.stringify([1, 2, 3]),
      });
    }

    setController({
      ...controller,
      page: 1,
      query: filters.query ?? controller.query,
      filters: backendFilters,
    });
  };

  // Fetch categories for ModuleFilter with tree structure
  useEffect(() => {
    dispatch(
      getProductCategoriesAsync({
        per_page: 1000,
        page: 1,
        parents: true, // Get categories with tree structure (parents with children)
      })
    );
  }, [dispatch]);

  // Store categories with tree structure
  const categoryTree = categoryState.data || [];

  // Derive ModuleFilter props from controller (must match absent filters: e.g. no warehouse row => lagerforte off)
  const getInitialModuleFilters = () => {
    const initialFilters = {
      query: controller.query || "",
      categories: [],
      active: controller.filters.some((f) => f.name === "active"),
      uncategorized: controller.filters.some((f) => f.name === "uncategorized"),
      lagerforte: controller.filters.some(
        (f) => f.name === "warehouse_status_type"
      ),
    };

    const categoryFilter = controller.filters.find((f) => f.name === "category");
    if (categoryFilter) {
      try {
        const categoryIds = JSON.parse(categoryFilter.value);
        initialFilters.categories = Array.isArray(categoryIds) ? categoryIds : [];
      } catch (e) {
        console.error("Error parsing category filter:", e);
      }
    }

    return initialFilters;
  };


  const [editModalOpen, setEditModalOpen] = useState(false);

  const [multiEditDialogOpen, setMultiEditDialogOpen] = useState(false);
  const [priceChangeDialogOpen, setPriceChangeDialogOpen] = useState(false);
  const [resetSelected, setResetSelected] = useState(false);

  const debouncedDispatchAction = useMemo(
    () =>
      debounce((ctrl) => {
        const payload = { ...ctrl };
        if (!ctrl.filters.some((f) => f.name === "active")) {
          payload.show_inactive = true;
        }
        dispatch(getProductsAsync(payload));
      }, 400),
    [dispatch]
  );

  useEffect(() => {
    return () => debouncedDispatchAction.cancel();
  }, [debouncedDispatchAction]);

  const handleResetSelected = () => {
    setResetSelected(true);
    setSelectedItems([]);

    setTimeout(() => {
      setResetSelected(false);
    }, 250);
  };

  useEffect(() => {
    if (multiEditDialogOpen) return;
    handleResetSelected();

    // Trigger the debounced dispatch action
    debouncedDispatchAction(controller);

    // Clean the debounced function on component unmount.
    return () => {
      debouncedDispatchAction.cancel();
    };
  }, [controller, multiEditDialogOpen, debouncedDispatchAction]);

  const headerActions = [];

  const headerMeta = [
    {
      label: productState.count + " Products",
      description: "wdw",
      path: null,
      action: null,
      icon: CheckBadgeIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: productState.newestProductsCount + " New products",
      description: "wdw",
      path: null,
      action: null,
      icon: ArrowUpIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
    {
      label: productState.synchedWithApiCount + " Synched with API",
      description: "wdw",
      path: null,
      action: null,
      icon: ArrowUpIcon,
      textColor: "text-teal-700",
      hidden: false,
    },
  ];


  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={"Products"}
        meta={headerMeta}
        mainButton={{
          label: "New",
          action: () => setEditModalOpen(true),
          path: null,
          color: "",
          icon: PlusIcon,
        }}
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            {/* <ProductsStats
              totalProductsCount={productState.count}
              newestProductsCount={productState.newestProductsCount}
            /> */}

            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleModuleFilterChange}
              initialFilters={getInitialModuleFilters()}
              searchPlaceholder={t("Search products...")}
              hideStatusFilter={true}
              categoryTree={categoryTree}
              customCheckboxButtonFilters={[
                {
                  key: "active",
                  label: t("Aktive"),
                  defaultValue: true,
                },
                {
                  key: "lagerforte",
                  label: t("Lagerførte"),
                  defaultValue: false,
                },
                {
                  key: "uncategorized",
                  label: t("Ikke kategoriserte"),
                  defaultValue: false,
                },
              ]}
              actionBar={
                <ProductActionEditBar
                  selectedItems={selectedItems}
                  setMultiEditDialogOpen={() => setMultiEditDialogOpen(true)}
                  setPriceChangeDialogOpen={() =>
                    setPriceChangeDialogOpen(true)
                  }
                />
              }
              selectedItems={selectedItems}
            />

            <ProductsTable
              onItemsSelect={(items) => setSelectedItems(items)}
              itemState={productState}
              itemsCount={productState.count}
              currentCount={productState.currentCount}
              page={controller.page}
              onPageChange={handlePageChange}
              perPage={controller.per_page}
              resetSelected={resetSelected}
              sortBy={controller.sortBy}
              sort={controller.sort}
              onSortChange={handleProductSort}
            />
          </div>
        </div>
      </DashboardHeader>

      <ProductModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
      />

      <ProductMultiEditDialog
        selectedItems={selectedItems}
        open={multiEditDialogOpen}
        onClose={() => setMultiEditDialogOpen(false)}
      />

      <ProductPriceChangeDialog
        selectedItems={selectedItems}
        open={priceChangeDialogOpen}
        onClose={() => setPriceChangeDialogOpen(false)}
      />
    </>
  );
};
